import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Loader2 } from 'lucide-react';
import { PrimaryButton } from './Buttons';
import { useSticker } from '../../context/StickerContext';
import { useToast } from './Toast';
import { uploadImage } from '../../api';
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:...;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
export function UploadDropzone() {
  const {
    uploadedFile,
    setUploadedFile,
    uploadState,
    setUploadState,
    sessionKey,
    setImagePath
  } = useSticker();
  const {
    addToast
  } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 1) {
      addToast('Only one image per order is allowed');
    }
    if (e.dataTransfer.files.length > 0) processUpload(e.dataTransfer.files[0]);
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) processUpload(e.target.files[0]);
  };
  const processUpload = async (file: File) => {
    setUploadState('loading');
    // Set the uploaded file info with local preview
    const fileInfo = {
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
      thumbnail: URL.createObjectURL(file),
      file: file
    };
    // If sessionKey exists, upload to server
    if (sessionKey) {
      try {
        const base64 = await fileToBase64(file);
        const path = await uploadImage(sessionKey, base64, file.name);
        setImagePath(path);
      } catch {
        // Upload to server failed, but local preview still works
        console.warn('Server upload failed, continuing with local preview');
      }
    }
    setUploadedFile(fileInfo);
    setUploadState('loaded');
    addToast('Upload complete');
  };
  const removeFile = () => {
    setUploadedFile(null);
    setUploadState('empty');
    setImagePath('');
    addToast('File removed');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  return <div className="w-full" data-id="element-434">
      <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept=".png,.jpg,.jpeg,.svg,.pdf,.ai,.eps" data-id="element-435" />

      {uploadState === 'empty' && <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-colors ${isDragging ? 'border-[#008696] bg-[#EAF6F7]' : 'border-[#D9D9D9] bg-gray-50'}`} data-id="element-436">
          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm mb-4" data-id="element-437">
            <Upload className="text-[#008696]" size={24} data-id="element-438" />
          </div>
          <h3 className="text-lg font-semibold text-[#111111] mb-1" data-id="element-439">
            Drop your files here
          </h3>
          <p className="text-sm text-[#5F5F5F] mb-6" data-id="element-440">or click to browse</p>
          <PrimaryButton onClick={() => fileInputRef.current?.click()} data-id="element-441">
            Choose Files
          </PrimaryButton>
          <div className="mt-6 text-xs text-[#5F5F5F] space-y-1" data-id="element-442">
            <p data-id="element-443">Raster and vector files supported.</p>
            <p data-id="element-444">Accepted: PNG, JPG, SVG, PDF, AI, EPS</p>
            <p className="text-[#5F5F5F]" data-id="element-445">
              High-resolution artwork recommended
            </p>
            <p className="text-[#FF0000] font-medium mt-2" data-id="element-446">
              One image per order. Each design must be submitted as a separate
              order.
            </p>
          </div>
        </div>}

      {uploadState === 'loading' && <div className="border border-[#D9D9D9] rounded-xl p-6 bg-gray-50" data-id="element-447">
          <div className="flex items-center gap-4" data-id="element-448">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-[#D9D9D9]" data-id="element-449">
              <Loader2 className="animate-spin text-[#008696]" size={24} data-id="element-450" />
            </div>
            <div className="flex-1" data-id="element-451">
              <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-452">
                <div className="h-full bg-[#008696] w-2/3 animate-pulse" data-id="element-453" />
              </div>
              <p className="text-sm text-[#5F5F5F] mt-2" data-id="element-454">Uploading...</p>
            </div>
          </div>
        </div>}

      {uploadState === 'loaded' && uploadedFile && <div className="border border-[#D9D9D9] rounded-xl p-4 bg-white relative group" data-id="element-455">
          <button onClick={removeFile} className="absolute top-2 right-2 p-1 bg-gray-100 rounded-full hover:bg-red-100 hover:text-red-600 transition-colors" data-id="element-456">
            <X size={16} data-id="element-457" />
          </button>
          <div className="flex items-center gap-4" data-id="element-458">
            <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center border border-[#D9D9D9]" data-id="element-459">
              {uploadedFile.name.endsWith('.png') || uploadedFile.name.endsWith('.jpg') ? <img src={uploadedFile.thumbnail} alt="Preview" className="w-full h-full object-cover" data-id="element-460" /> : <FileText className="text-gray-400" size={32} data-id="element-461" />}
            </div>
            <div data-id="element-462">
              <p className="font-medium text-[#111111] truncate max-w-[200px]" data-id="element-463">
                {uploadedFile.name}
              </p>
              <p className="text-sm text-[#5F5F5F]" data-id="element-464">{uploadedFile.size}</p>
            </div>
          </div>
        </div>}
    </div>;
}